/**
 * Locale-aware formatting.
 *
 * Everything here used to be `toLocaleTimeString('he-IL', …)`. A hardcoded
 * locale does not just show the wrong language -- it forces one calendar, one
 * numbering system and one clock convention on every user. A Hindi user gets
 * Latin digits where their locale uses Devanagari; a US user gets 24-hour time.
 * None of that is visible to anyone testing in one language.
 *
 * `Intl` formatters are expensive to construct and cheap to reuse, so they are
 * memoised per locale rather than built per message. A conversation renders
 * hundreds of timestamps.
 */
import { directionOf, negotiateLocale } from '@vav/protocol';

/** Locales we ship catalogues for. Extend as translations land. */
export const SUPPORTED_LOCALES = ['en', 'he', 'ar', 'es', 'pt-BR', 'hi', 'ja'] as const;

let activeLocale = negotiateLocale(SUPPORTED_LOCALES, 'en');

export function getLocale(): string {
  return activeLocale;
}

export function setLocale(locale: string): void {
  activeLocale = locale;
  caches.clear();
  applyDocumentLocale();
}

/**
 * Put the active locale on the document.
 *
 * `dir` drives every logical CSS property in the stylesheet, so this is what
 * makes the layout mirror. `lang` drives font selection, hyphenation and screen
 * reader pronunciation. Both were previously hardcoded to Hebrew in the HTML.
 */
export function applyDocumentLocale(override?: string): void {
  if (override) {
    activeLocale = override;
    caches.clear();
  }
  if (typeof document === 'undefined') return;
  document.documentElement.lang = activeLocale;
  document.documentElement.dir = directionOf(activeLocale);
}

const caches = new Map<string, Intl.DateTimeFormat | Intl.NumberFormat | Intl.RelativeTimeFormat>();

function dateTime(options: Intl.DateTimeFormatOptions, key: string): Intl.DateTimeFormat {
  const cacheKey = `${activeLocale}:${key}`;
  let cached = caches.get(cacheKey) as Intl.DateTimeFormat | undefined;
  if (!cached) {
    cached = new Intl.DateTimeFormat(activeLocale, options);
    caches.set(cacheKey, cached);
  }
  return cached;
}

/** Clock time. 12- or 24-hour is decided by the locale, never by us. */
export function formatTime(ts: number): string {
  return dateTime({ hour: 'numeric', minute: '2-digit' }, 'time').format(ts);
}

export function formatWeekday(ts: number): string {
  return dateTime({ weekday: 'short' }, 'weekday').format(ts);
}

export function formatShortDate(ts: number): string {
  return dateTime({ day: 'numeric', month: 'numeric' }, 'shortDate').format(ts);
}

export function formatFullDate(ts: number): string {
  return dateTime({ day: 'numeric', month: 'long', year: 'numeric' }, 'fullDate').format(ts);
}

/** Day and month without the year, for dates inside the current year. */
export function formatDayMonth(ts: number): string {
  return dateTime({ day: 'numeric', month: 'long' }, 'dayMonth').format(ts);
}

export function formatNumber(value: number): string {
  const cacheKey = `${activeLocale}:number`;
  let cached = caches.get(cacheKey) as Intl.NumberFormat | undefined;
  if (!cached) {
    cached = new Intl.NumberFormat(activeLocale);
    caches.set(cacheKey, cached);
  }
  return cached.format(value);
}

/**
 * Byte sizes.
 *
 * `Intl.NumberFormat` with `unit: 'byte'` handles both the number formatting
 * and the unit name in the user's language, which hand-built `${n} MB` cannot.
 */
export function formatBytes(bytes: number): string {
  const units: Intl.NumberFormatOptions['unit'][] = ['byte', 'kilobyte', 'megabyte', 'gigabyte'];
  let value = bytes;
  let step = 0;
  while (value >= 1024 && step < units.length - 1) {
    value /= 1024;
    step += 1;
  }
  try {
    return new Intl.NumberFormat(activeLocale, {
      style: 'unit',
      unit: units[step],
      unitDisplay: 'short',
      maximumFractionDigits: step === 0 ? 0 : 1,
    }).format(value);
  } catch {
    // `style: 'unit'` is widely supported but not universally; a readable
    // fallback beats throwing inside a message list.
    return `${value.toFixed(step === 0 ? 0 : 1)} ${String(units[step])}`;
  }
}
